/**
 * Merlin Graph Explorer - Unified Entry Point
 * 
 * This is THE SINGLE ENTRY POINT for the Graph extension.
 * 
 * Exports:
 * - MerlinGraph: Full graph visualization with API integration and UI modules
 * - GraphExplorer: Core vis.js wrapper (for advanced usage)
 * - DashGraph: React component for Dash integration
 * 
 * Architecture:
 * - core/GraphExplorerMinimal.js: Core vis.js wrapper (network, data, physics)
 * - modules/explorer-*.js: UI components (toolbar, tabs, windows, panels)
 * - modules/explorer-constants.js: Shared configuration
 * 
 * Features:
 * - Full GraphExplorer UI (tabs, toolbar, windows)
 * - API integration for cursor-based graph loading
 * - Authentication (Bearer token / API key)
 * - Query execution
 * - Polling for real-time updates
 * - EntityInput from @merlin/search extension
 * - Sentry error tracking in production
 */

// Import minimal core
import { GraphExplorer } from './core/GraphExplorerMinimal.js';

// Import Sentry for error tracking
import { initSentry, captureError, setUser, addBreadcrumb, setExtra } from '../../lib/js/sentry.js';

// Initialize Sentry for production environment
// DSN should be configured via environment variable or config
const SENTRY_DSN = typeof window !== 'undefined' && window.SENTRY_EXTENSIONS_DSN 
    ? window.SENTRY_EXTENSIONS_DSN 
    : (typeof process !== 'undefined' && process.env?.SENTRY_EXTENSIONS_DSN);

if (SENTRY_DSN) {
    initSentry({
        dsn: SENTRY_DSN,
        extension: 'graph',
        version: '1.0.0',
        tracesSampleRate: 0.1,
    }).then(enabled => {
        if (enabled) {
            console.log('[MerlinGraph] Sentry error tracking enabled');
        }
    });
}

// Import styles
import './styles/base.css';
import './styles/toolbar.css';
import './styles/panels.css';
import './styles/tabs.css';

// Import constants (these attach to window)
import './modules/explorer-constants.js';

// Import all UI modules (these attach to window)
import './modules/explorer-utils.js';
import './modules/explorer-core.js';
import './modules/explorer-clustering.js';
import './modules/explorer-api.js';
import './modules/explorer-toolbar.js';
import './modules/explorer-tabs.js';
import './modules/explorer-windows.js';
import './modules/explorer-panels.js';
import './modules/explorer-keyboard.js';
import './modules/explorer-hover-menu.js';
import './modules/explorer-modal.js';
import './modules/explorer-timeline.js';
import './modules/explorer-message-renderer.js';
import './modules/explorer-cursor.js';
import './modules/explorer-chat.js';


/**
 * MerlinGraph - Standalone graph visualization with API integration
 * 
 * Usage:
 *   // With API connection
 *   const graph = new MerlinGraph('#container', {
 *     api_endpoint: 'http://localhost:3000',
 *     workspace_id: 'ws-123',
 *     cursor_id: 'cur-456',
 *     theme: 'dark',
 *   });
 *   await graph.ready();
 *   await graph.fetchAndUpdate();
 *   
 *   // Or with direct data:
 *   const graph = new MerlinGraph('#container', { theme: 'dark' });
 *   await graph.ready();
 *   graph.setData({ nodes: [...], edges: [...] });
 */
export class MerlinGraph {
    constructor(containerSelector, options = {}) {
        this.container = typeof containerSelector === 'string' 
            ? document.querySelector(containerSelector)
            : containerSelector;
            
        if (!this.container) {
            throw new Error(`Container not found: ${containerSelector}`);
        }
        
        this.options = this._resolveOptions(options);
        
        // API state
        this.apiState = {
            isLoading: false,
            lastUpdate: null,
            lastDataHash: null,
            pollingHandle: null,
            authToken: null,
            apiKey: null,
            authMethod: null,
            aliases: [],
        };
        
        this.explorer = null;
        this._initPromise = this._init();
    }

    _resolveOptions(options = {}) {
        const base = {
            theme: 'light',
            ui_mode: 'full',
            show_legend: true,
            show_fql: false,
            show_toolbar: true,
            show_tabs: true,
            show_bottom_bar: true,
            show_cursor: true,
            show_timeline: false,
            physics_enabled: true,
            physics_solver: 'forceAtlas2Based',
            extension_mode: false,
            api_endpoint: null,
            workspace_id: null,
            cursor_id: 'me',  // Default to "me" - API resolves to user's default cursor
            instance_id: null,
            polling_enabled: false,
            polling_interval_ms: 5000,
            auth_token: null,  // Bearer token passed from parent
            api_key: null,     // API key passed from parent
            onSelectionChange: null,
            onExecuteQuery: null,
            onOperator: null,
            onAlgorithm: null,
        };

        const uiMode = options.ui_mode || base.ui_mode;
        const preset = (typeof window !== 'undefined' && window.ExplorerConstants?.UI_MODE_PRESETS?.[uiMode])
            || {};
        const { toolbar: presetToolbar, ...presetOptions } = preset;

        return {
            ...base,
            ...presetOptions,
            ...options,
            ui_mode: uiMode,
            _toolbarConfig: options.toolbar || presetToolbar || null,
        };
    }
    
    async _init() {
        // Load authentication - first from options, then from session storage
        this._loadAuth();
        
        // Create explorer instance with callbacks
        this.explorer = new GraphExplorer(this.container, {
            ...this.options,
            onSelectionChange: (selection) => this._handleSelectionChange(selection),
            onExecuteQuery: (query) => this._handleExecuteQuery(query),
            onOperator: (op, params) => this._handleOperator(op, params),
            onAlgorithm: (category, algo, nodes) => this._handleAlgorithm(category, algo, nodes),
        });
        
        // Wait for explorer to be ready
        await this.explorer.ready();
        
        // Initialize UI modules
        this._initModules();
        
        console.log('[MerlinGraph] Initialized', {
            api_endpoint: this.options.api_endpoint,
            workspace_id: this.options.workspace_id,
            cursor_id: this.options.cursor_id,
            authMethod: this.apiState.authMethod,
        });
    }
    
    /**
     * Initialize UI modules
     */
    _initModules() {
        const config = {
            network: this.explorer.network,
            nodes: this.explorer.nodes,
            edges: this.explorer.edges,
            config: {
                apiEndpoint: this.options.api_endpoint,
                workspaceId: this.options.workspace_id,
                cursorId: this.options.cursor_id,
                instanceId: this.options.instance_id,
                theme: this.options.theme,
                physicsEnabled: this.options.physics_enabled,
                extensionMode: this.options.extension_mode
            }
        };
        
        // Initialize modules in order
        if (window.ExplorerCore) {
            window.ExplorerCore.init(config);
        }
        
        if (window.ExplorerAPI) {
            window.ExplorerAPI.init({
                apiEndpoint: config.config.apiEndpoint,
                workspaceId: config.config.workspaceId,
                cursorId: config.config.cursorId,
                instanceId: config.config.instanceId,
                // Pass auth from apiState (loaded from options or session)
                authToken: this.apiState.authToken,
                apiKey: this.apiState.apiKey
            });
            // Configure server-side layout from options
            if (this.options.layout_algorithm) {
                window.ExplorerAPI.setLayoutAlgorithm(this.options.layout_algorithm);
            }
            if (this.options.auto_layout !== undefined) {
                window.ExplorerAPI.setAutoLayout(this.options.auto_layout);
            }
        }
        
        // Initialize clustering module
        if (window.ExplorerClustering) {
            window.ExplorerClustering.init({
                autoCluster: this.options.auto_cluster !== undefined ? this.options.auto_cluster : true,
                clusterThreshold: this.options.cluster_threshold || 500,
                clusterAttribute: this.options.cluster_attribute || 'resource_name',
                minClusterSize: this.options.min_cluster_size || 3,
            });
        }
        
        // Only show toolbar if enabled
        if (window.ExplorerToolbar && this.options.show_toolbar !== false) {
            window.ExplorerToolbar.init(this.options._toolbarConfig || {});
        }
        
        // Only show tabs if enabled (disabled when embedded in another tab system)
        if (window.ExplorerTabs && this.options.show_tabs !== false) {
            window.ExplorerTabs.init();
        } else if (
            window.ExplorerTabs
            && this.options.show_bottom_bar !== false
            && typeof window.ExplorerTabs.initBottomBar === 'function'
        ) {
            window.ExplorerTabs.initBottomBar();
        }
        
        if (window.ExplorerWindows) {
            window.ExplorerWindows.init();
        }
        
        if (window.ExplorerPanels) {
            window.ExplorerPanels.init();
        }
        
        if (window.ExplorerKeyboard) {
            window.ExplorerKeyboard.init();
        }
        
        if (window.ExplorerHoverMenu) {
            window.ExplorerHoverMenu.init();
        }
        
        if (window.ExplorerModal) {
            window.ExplorerModal.init();
        }
        
        // Initialize Cursor module (cursor dropdown, save, reset)
        if (window.ExplorerCursor && this.options.show_cursor !== false) {
            window.ExplorerCursor.init({
                apiEndpoint: config.config.apiEndpoint,
                workspaceId: config.config.workspaceId,
                cursorId: config.config.cursorId
            });
        }
        
        // Initialize Chat module (split-screen A2UIChat panel)
        if (window.ExplorerChat) {
            if (this.options.show_chat !== false) {
                window.ExplorerChat.init({
                    apiEndpoint: config.config.apiEndpoint,
                    workspaceId: config.config.workspaceId,
                    cursorId: config.config.cursorId,
                    theme: config.config.theme,
                    accessToken: this.apiState.authToken
                });
            } else {
                // Clean up chat from any previous init
                window.ExplorerChat.destroy();
            }
        }
        
        // Initialize Lucide icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }

        this._bindCursorChangedHandler();
        
        // When tabs are disabled, show graph-only toolbar elements
        // This must happen AFTER all modules are initialized and icons are created
        if (this.options.show_tabs === false) {
            // Use requestAnimationFrame to ensure DOM is fully rendered
            requestAnimationFrame(() => {
                this._showGraphToolbarElements();
            });
        }
    }
    
    /**
     * Wait for initialization to complete
     */
    async ready() {
        return this._initPromise;
    }
    
    _bindCursorChangedHandler() {
        if (this._cursorChangedHandler) return;
        this._cursorChangedHandler = (event) => {
            const cursorId = event?.detail?.id;
            if (!cursorId || cursorId === this.options.cursor_id) return;
            this.options.cursor_id = cursorId;
        };
        document.addEventListener('explorer:cursorChanged', this._cursorChangedHandler);
    }

    /**
     * Show graph-only toolbar elements when tabs are disabled
     * This is needed because ExplorerTabs.switchTab('graph') normally shows these
     */
    _showGraphToolbarElements() {
        // Query within the container to avoid affecting other instances
        const container = this.container;
        const graphOnlyElements = container.querySelectorAll('.toolbar-graph-only');
        const graphBookmarksElements = container.querySelectorAll('.toolbar-graph-bookmarks');
        
        [...graphOnlyElements, ...graphBookmarksElements].forEach((el) => {
            el.style.display = '';
            el.style.opacity = '1';
            el.style.maxHeight = 'none';
        });
        
        const dividers = container.querySelectorAll('.toolbar-divider.toolbar-graph-only, .toolbar-divider.toolbar-graph-bookmarks');
        dividers.forEach((el) => {
            el.style.display = '';
        });
    }
    
    // ============================================
    // Callback Handlers
    // ============================================
    
    _handleSelectionChange(selection) {
        const { nodes = [], edges = [] } = selection || {};
        
        // Manage visual dimming/restoring via ExplorerCore (single source of truth)
        // This ensures all selection paths (canvas click, Find, Alias, etc.) go through
        // one system, preventing orphaned dim states.
        if (typeof window.GraphExplorer !== 'undefined') {
            if (nodes.length > 0) {
                // dimNonSelectedNodes is idempotent (stores originals only once)
                window.GraphExplorer.dimNonSelectedNodes(nodes);
                if (window.GraphExplorer.state) {
                    window.GraphExplorer.state.selectedNodes = nodes;
                    window.GraphExplorer.state.selectedEdges = edges;
                }
            } else {
                // Fully restore all nodes - clears font, opacity, borders, edges
                window.GraphExplorer.restoreAllNodes();
            }
        }
        
        // Update Selection (details) window with selected nodes
        if (typeof window.ExplorerWindows !== 'undefined') {
            if (nodes.length > 0 && this.explorer?.nodes) {
                // Get node data for all selected nodes
                const nodeDataArray = nodes.map(id => this.explorer.nodes.get(id)).filter(Boolean);
                if (window.ExplorerWindows.showNodeDetails) {
                    window.ExplorerWindows.showNodeDetails(nodeDataArray);
                }
            } else {
                // Clear selection details
                if (window.ExplorerWindows.clearDetails) {
                    window.ExplorerWindows.clearDetails();
                }
            }
            
            // Update operator states based on selection count
            if (window.ExplorerWindows.updateOperatorStates) {
                window.ExplorerWindows.updateOperatorStates(nodes.length);
            }
        }
        
        // Call user callback if provided
        if (this.options.onSelectionChange) {
            this.options.onSelectionChange(selection);
        }
    }
    
    _handleExecuteQuery(query) {
        if (this.options.onExecuteQuery) {
            return this.options.onExecuteQuery(query);
        }
        // Default: execute via API
        return this.executeQuery(query);
    }
    
    _handleOperator(op, params) {
        if (this.options.onOperator) {
            return this.options.onOperator(op, params);
        }
        // Default: execute via API
        return this.executeOperator(op, params);
    }
    
    _handleAlgorithm(category, algo, nodes) {
        if (this.options.onAlgorithm) {
            return this.options.onAlgorithm(category, algo, nodes);
        }
        console.log('[MerlinGraph] Algorithm:', category, algo, nodes);
    }
    
    // ============================================
    // Authentication
    // ============================================
    
    _loadAuth() {
        // First check if auth was passed via options
        if (this.options.auth_token) {
            this.apiState.authToken = this.options.auth_token;
            this.apiState.authMethod = 'bearer';
            console.log('[MerlinGraph] Bearer token loaded from options');
            return;
        }
        
        if (this.options.api_key) {
            this.apiState.apiKey = this.options.api_key;
            this.apiState.authMethod = 'apikey';
            console.log('[MerlinGraph] API key loaded from options');
            return;
        }
        
        // Try to load Bearer token from session storage
        try {
            const userStr = sessionStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                if (user && user.token) {
                    this.apiState.authToken = user.token;
                    this.apiState.authMethod = 'bearer';
                    console.log('[MerlinGraph] Bearer token loaded from session');
                    return;
                }
            }
        } catch (e) {
            console.warn('[MerlinGraph] Failed to parse user from session:', e);
        }
        
        // Try API key from session storage
        const storedApiKey = sessionStorage.getItem('explorerApiKey');
        if (storedApiKey) {
            this.apiState.apiKey = storedApiKey;
            this.apiState.authMethod = 'apikey';
            console.log('[MerlinGraph] API key loaded from session');
            return;
        }
        
        this.apiState.authMethod = null;
    }
    
    /**
     * Set API key for authentication
     */
    setApiKey(apiKey) {
        if (apiKey && apiKey.trim()) {
            this.apiState.apiKey = apiKey.trim();
            this.apiState.authMethod = 'apikey';
            sessionStorage.setItem('explorerApiKey', this.apiState.apiKey);
            console.log('[MerlinGraph] API key set');
            return true;
        }
        return false;
    }
    
    /**
     * Clear stored API key
     */
    clearApiKey() {
        this.apiState.apiKey = null;
        if (this.apiState.authMethod === 'apikey') {
            this.apiState.authMethod = null;
        }
        sessionStorage.removeItem('explorerApiKey');
    }
    
    /**
     * Check if authentication is available
     */
    hasAuth() {
        return this.apiState.authMethod !== null;
    }
    
    _getAuthHeaders() {
        const headers = { 'Content-Type': 'application/json' };
        if (this.apiState.authMethod === 'bearer' && this.apiState.authToken) {
            headers['Authorization'] = `Bearer ${this.apiState.authToken}`;
        } else if (this.apiState.authMethod === 'apikey' && this.apiState.apiKey) {
            headers['X-API-Key'] = this.apiState.apiKey;
        }
        return headers;
    }
    
    // ============================================
    // Workspace Management
    // ============================================

    /**
     * Switch to a different workspace.
     * Updates all internal state (options, ExplorerAPI, ExplorerTasks),
     * resets the cursor, clears the graph, and reloads data.
     *
     * @param {string} workspaceId - New workspace ID
     * @param {Object} [opts] - Optional overrides
     * @param {string} [opts.cursor_id] - Cursor to use (default: 'me')
     * @param {boolean} [opts.resetCursor] - Whether to reset cursor (default: true)
     * @param {boolean} [opts.reload] - Whether to fetch data after switch (default: true)
     */
    async setWorkspace(workspaceId, opts = {}) {
        if (!workspaceId) return;
        const prevWorkspaceId = this.options.workspace_id;
        if (workspaceId === prevWorkspaceId) return;

        const cursorId = opts.cursor_id || 'me';
        const shouldReset = opts.resetCursor !== false;
        const shouldReload = opts.reload !== false;

        console.log(`[MerlinGraph] Switching workspace: ${prevWorkspaceId} -> ${workspaceId}`);

        // 1. Update own options
        this.options.workspace_id = workspaceId;
        this.options.cursor_id = cursorId;

        // 1b. Sync GraphExplorer options (used by search suggestions, etc.)
        if (this.explorer && this.explorer.options) {
            this.explorer.options.workspace_id = workspaceId;
            this.explorer.options.workspaceId = workspaceId;
            this.explorer.options.cursor_id = cursorId;
        }

        // 2. Sync ExplorerAPI (used by all modules for API calls)
        if (window.ExplorerAPI && window.ExplorerAPI.setConfig) {
            window.ExplorerAPI.setConfig({
                workspaceId: workspaceId,
                cursorId: cursorId,
            });
        }

        // 3. Clear existing graph data
        this.explorer.setData([], []);

        // 4. Reset cursor for the new workspace
        if (shouldReset) {
            try {
                await this.resetCursor();
            } catch (e) {
                console.warn('[MerlinGraph] Cursor reset failed during workspace switch:', e);
            }
        }

        // 5. Reload graph data from the new workspace
        if (shouldReload) {
            try {
                await this.fetchAndUpdate();
            } catch (e) {
                console.warn('[MerlinGraph] Failed to load data for new workspace:', e);
            }
        }

        // 6. Close chat panel from the previous workspace
        if (window.ExplorerChat && typeof window.ExplorerChat.close === 'function') {
            window.ExplorerChat.close();
        }

        // 7. Reload cursor data and chat workspace
        if (window.ExplorerCursor) {
            if (typeof window.ExplorerCursor.loadCursors === 'function') window.ExplorerCursor.loadCursors();
        }
        if (window.ExplorerChat) {
            if (typeof window.ExplorerChat.setChatWorkspace === 'function') {
                window.ExplorerChat.setChatWorkspace(workspaceId, this.options.api_endpoint, this.apiState.authToken);
            }
        }

        // 8. Reload patterns list (workspace-scoped)
        if (window.ExplorerWindows && typeof window.ExplorerWindows.loadPatterns === 'function') {
            window.ExplorerWindows.loadPatterns();
        }

        // 9. Reload checkpoints/bookmarks for the new workspace's cursor
        if (window.ExplorerToolbar && typeof window.ExplorerToolbar.loadCheckpointsAndBookmarks === 'function') {
            window.ExplorerToolbar.loadCheckpointsAndBookmarks();
        }
        if (window.ExplorerWindows && typeof window.ExplorerWindows.loadCheckpointsAndBookmarks === 'function') {
            window.ExplorerWindows.loadCheckpointsAndBookmarks();
        }

        console.log(`[MerlinGraph] Workspace switch complete: ${workspaceId}`);
    }

    // ============================================
    // API Methods
    // ============================================
    
    /**
     * Check if API is configured
     */
    isConfigured() {
        // Empty string is valid for api_endpoint (dev mode with Vite proxy)
        const hasEndpoint = this.options.api_endpoint !== null && this.options.api_endpoint !== undefined;
        return hasEndpoint && !!this.options.workspace_id;
    }
    
    /**
     * Reset cursor state/history/params
     */
    async resetCursor() {
        if (!this.isConfigured() || !this.options.cursor_id) {
            console.warn('[MerlinGraph] Cannot reset cursor: missing API configuration');
            return null;
        }
        
        try {
            const url = `${this.options.api_endpoint}/workspaces/${this.options.workspace_id}/cursors/${this.options.cursor_id}/reset`;
            const response = await fetch(url, {
                method: 'POST',
                headers: this._getAuthHeaders()
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            console.log('[MerlinGraph] Cursor reset successful');
            return await response.json();
            
        } catch (error) {
            console.warn('[MerlinGraph] Cursor reset failed (may not exist yet):', error);
            return null;
        }
    }
    
    /**
     * Fetch graph data from API.
     * Delegates to ExplorerAPI.refreshGraph() when available so that all
     * settings (max_nodes, layout, layout_config, filters, etc.) are
     * applied to the request.  Falls back to a bare fetch only when
     * ExplorerAPI has not been initialised yet.
     */
    async fetchAndUpdate() {
        if (!this.isConfigured() || !this.options.cursor_id) {
            console.warn('[MerlinGraph] Cannot fetch: missing API configuration');
            return null;
        }

        // --- Preferred path: use ExplorerAPI (includes all settings) ---
        if (typeof window.ExplorerAPI !== 'undefined' &&
            typeof window.ExplorerAPI.refreshGraph === 'function' &&
            window.ExplorerAPI.isConfigured && window.ExplorerAPI.isConfigured()) {
            console.log('[MerlinGraph] Delegating fetchAndUpdate → ExplorerAPI.refreshGraph');
            await window.ExplorerAPI.refreshGraph(true);
            this.apiState.lastUpdate = new Date();
            return true;
        }

        // --- Fallback: raw fetch (ExplorerAPI not ready yet) ---
        this.apiState.isLoading = true;
        this.explorer._showLoading(null, { sequential: true });
        
        addBreadcrumb({
            category: 'api',
            message: 'Fetching graph data (fallback)',
            data: {
                workspace_id: this.options.workspace_id,
                cursor_id: this.options.cursor_id,
            }
        });
        
        try {
            const url = `${this.options.api_endpoint}/workspaces/${this.options.workspace_id}/cursors/${this.options.cursor_id}/graph/data`;
            const response = await fetch(url, {
                headers: this._getAuthHeaders()
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            this.explorer._hideLoading();
            this.explorer.setData(data.nodes || [], data.edges || []);

            // Apply color mapping and update legend (same pipeline as ExplorerAPI.updateGraph)
            if (window.GraphExplorer) {
                const colorAttr = window.GraphExplorer.state?.currentColorAttribute || 'resource_name';
                if (typeof window.GraphExplorer.setColorByAttribute === 'function') {
                    window.GraphExplorer.setColorByAttribute(colorAttr);
                }
                if (typeof window.GraphExplorer.setEdgeColorByLabel === 'function') {
                    window.GraphExplorer.setEdgeColorByLabel();
                }
                if (typeof window.GraphExplorer.buildInitialLegend === 'function') {
                    window.GraphExplorer.buildInitialLegend();
                }
            }
            setTimeout(() => {
                if (window.ExplorerWindows?.updateLegend) {
                    window.ExplorerWindows.updateLegend();
                }
            }, 0);
            
            this.apiState.lastUpdate = new Date();
            this.apiState.isLoading = false;
            
            return data;
            
        } catch (error) {
            console.error('[MerlinGraph] Fetch failed:', error);
            captureError(error, {
                tags: { operation: 'fetchAndUpdate' },
                extra: {
                    workspace_id: this.options.workspace_id,
                    cursor_id: this.options.cursor_id,
                    api_endpoint: this.options.api_endpoint,
                }
            });
            this.apiState.isLoading = false;
            this.explorer._hideLoading();
            throw error;
        }
    }
    
    /**
     * Execute a query
     */
    async executeQuery(query) {
        if (!this.isConfigured()) {
            throw new Error('API not configured');
        }
        
        addBreadcrumb({
            category: 'api',
            message: 'Executing query',
            data: { query: query.substring(0, 100) }
        });
        
        try {
            const url = `${this.options.api_endpoint}/workspaces/${this.options.workspace_id}/query`;
            const response = await fetch(url, {
                method: 'POST',
                headers: this._getAuthHeaders(),
                body: JSON.stringify({ query, cursor_id: this.options.cursor_id })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            return await response.json();
            
        } catch (error) {
            console.error('[MerlinGraph] Query failed:', error);
            captureError(error, {
                tags: { operation: 'executeQuery' },
                extra: {
                    workspace_id: this.options.workspace_id,
                    cursor_id: this.options.cursor_id,
                    query: query.substring(0, 200),
                }
            });
            throw error;
        }
    }
    
    /**
     * Execute an operator
     */
    async executeOperator(operator, params) {
        if (!this.isConfigured()) {
            throw new Error('API not configured');
        }
        
        addBreadcrumb({
            category: 'api',
            message: `Executing operator: ${operator}`,
            data: { operator, params }
        });
        
        try {
            const url = `${this.options.api_endpoint}/workspaces/${this.options.workspace_id}/cursors/${this.options.cursor_id}/operators/${operator}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: this._getAuthHeaders(),
                body: JSON.stringify({ params })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const result = await response.json();
            
            // Refresh graph data after operator
            await this.fetchAndUpdate();
            
            return result;
            
        } catch (error) {
            console.error('[MerlinGraph] Operator failed:', error);
            captureError(error, {
                tags: { operation: 'executeOperator', operator },
                extra: {
                    workspace_id: this.options.workspace_id,
                    cursor_id: this.options.cursor_id,
                    params,
                }
            });
            throw error;
        }
    }
    
    // ============================================
    // Polling
    // ============================================
    
    /**
     * Start polling for updates
     */
    startPolling(intervalMs = this.options.polling_interval_ms) {
        if (this.apiState.pollingHandle) {
            this.stopPolling();
        }
        
        this.apiState.pollingHandle = setInterval(() => {
            this.fetchAndUpdate().catch(err => {
                console.warn('[MerlinGraph] Polling fetch failed:', err);
            });
        }, intervalMs);
        
        console.log('[MerlinGraph] Polling started:', intervalMs, 'ms');
    }
    
    /**
     * Stop polling
     */
    stopPolling() {
        if (this.apiState.pollingHandle) {
            clearInterval(this.apiState.pollingHandle);
            this.apiState.pollingHandle = null;
            console.log('[MerlinGraph] Polling stopped');
        }
    }
    
    // ============================================
    // Data Methods (Proxy to explorer)
    // ============================================
    
    setData(data) {
        const nodes = Array.isArray(data) ? data : (data.nodes || []);
        const edges = Array.isArray(data) ? [] : (data.edges || []);
        return this.explorer.setData(nodes, edges);
    }

    /** Apply default node/edge coloring and legend after a direct setData load. */
    _applyGraphAppearance() {
        if (!window.GraphExplorer) return;
        const colorAttr = this.options.color_attribute
            || window.GraphExplorer.state?.currentColorAttribute
            || 'resource_name';
        if (typeof window.GraphExplorer.setColorByAttribute === 'function') {
            window.GraphExplorer.setColorByAttribute(colorAttr);
        }
        if (typeof window.GraphExplorer.setEdgeColorByLabel === 'function') {
            window.GraphExplorer.setEdgeColorByLabel();
        }
        if (typeof window.GraphExplorer.buildInitialLegend === 'function') {
            window.GraphExplorer.buildInitialLegend();
        }
        setTimeout(() => {
            if (window.ExplorerWindows?.updateLegend) {
                window.ExplorerWindows.updateLegend();
            }
        }, 0);
    }
    
    setNodes(nodes) {
        return this.explorer.setNodes(nodes);
    }
    
    setEdges(edges) {
        return this.explorer.setEdges(edges);
    }
    
    getData() {
        return this.explorer.getData();
    }
    
    getSelection() {
        return this.explorer.getSelection();
    }
    
    // ============================================
    // View Controls (Proxy to explorer)
    // ============================================
    
    setTheme(theme) {
        const result = this.explorer.setTheme(theme);
        // Sync theme to chat panel (if open)
        if (window.ExplorerChat?.setChatTheme) {
            window.ExplorerChat.setChatTheme(theme);
        }
        return result;
    }
    
    setPhysics(enabled) {
        return this.explorer.setPhysics(enabled);
    }
    
    resetView() {
        return this.explorer.resetView();
    }
    
    fit(options) {
        return this.explorer.fit(options);
    }
    
    // ============================================
    // Physics Solvers (Proxy to explorer)
    // ============================================
    
    barnesHut(params) {
        return this.explorer.barnesHut(params);
    }
    
    repulsion(params) {
        return this.explorer.repulsion(params);
    }
    
    hrepulsion(params) {
        return this.explorer.hrepulsion(params);
    }
    
    forceAtlas2Based(params) {
        return this.explorer.forceAtlas2Based(params);
    }
    
    // ============================================
    // Layout (Proxy to explorer)
    // ============================================
    
    setHierarchical(options) {
        return this.explorer.setHierarchical(options);
    }
    
    disableHierarchical() {
        return this.explorer.disableHierarchical();
    }
    
    // ============================================
    // Edge Configuration (Proxy to explorer)
    // ============================================
    
    setEdgeSmooth(type) {
        return this.explorer.setEdgeSmooth(type);
    }
    
    inheritEdgeColors(status) {
        return this.explorer.inheritEdgeColors(status);
    }
    
    // ============================================
    // vis.js Options (Proxy to explorer)
    // ============================================
    
    setOptions(options) {
        return this.explorer.setOptions(options);
    }
    
    showButtons(filter) {
        return this.explorer.showButtons(filter);
    }
    
    hideButtons() {
        return this.explorer.hideButtons();
    }
    
    // ============================================
    // Cursor Loading
    // ============================================
    
    async loadCursor(cursorId, opts = {}) {
        this.options.cursor_id = cursorId;
        if (window.ExplorerAPI && window.ExplorerAPI.setConfig) {
            window.ExplorerAPI.setConfig({ cursorId });
        }
        if (window.ExplorerCursor && window.ExplorerCursor.syncCursorSelection) {
            window.ExplorerCursor.syncCursorSelection(cursorId);
        }

        const initialBookmark = opts.initial_bookmark || this.options.initial_bookmark;
        const bootstrapBookmarks = opts.task_context?.cursor_bootstrap?.bookmarks || {};
        const bookmarkNames = Object.keys(bootstrapBookmarks);

        if (
            initialBookmark
            && opts.apply_bootstrap === false
            && window.ExplorerAPI?.fetchGraphDataForPointer
        ) {
            if (this.explorer && typeof this.explorer._showLoading === 'function') {
                this.explorer._showLoading(null, { sequential: true });
            }
            let pointerData;
            try {
                pointerData = await window.ExplorerAPI.fetchGraphDataForPointer('bookmark', initialBookmark);
            } finally {
                if (this.explorer && typeof this.explorer._hideLoading === 'function') {
                    this.explorer._hideLoading();
                }
            }
            const expectedMeta = bootstrapBookmarks[initialBookmark] || {};
            const expectedNodes = Number(expectedMeta.node_count || 0);
            const actualNodes = Array.isArray(pointerData?.nodes) ? pointerData.nodes.length : 0;
            const pointerLooksValid = pointerData
                && !pointerData.error
                && (actualNodes > 0 || expectedNodes === 0);

            if (pointerLooksValid) {
                this.setData(pointerData);
                this._applyGraphAppearance();
                const bookmarkList = (bookmarkNames.length ? bookmarkNames : [initialBookmark])
                    .map((name) => ({ name }));
                if (typeof window.ExplorerTabs !== 'undefined' && window.ExplorerTabs.setBookmarks) {
                    window.ExplorerTabs.setBookmarks(bookmarkList, initialBookmark);
                }
                if (bookmarkNames.length > 1 && window.ExplorerAPI?.prefetchAllPointers) {
                    const remaining = bookmarkNames.filter((name) => name !== initialBookmark);
                    window.ExplorerAPI.prefetchAllPointers([], remaining).catch(() => {});
                }
                if (typeof window.ExplorerAPI.fetchCursorState === 'function') {
                    window.ExplorerAPI.fetchCursorState()
                        .then((stateData) => {
                            if (stateData && window.ExplorerAPI.refreshCursorPointers) {
                                return window.ExplorerAPI.refreshCursorPointers(stateData);
                            }
                            return null;
                        })
                        .catch(() => {});
                }
                return this;
            }
        }

        await this.fetchAndUpdate();
        if (initialBookmark && window.ExplorerAPI?.navigateToBookmark) {
            await window.ExplorerAPI.navigateToBookmark(initialBookmark, { switchTab: false, previewOnly: true });
        } else if (
            opts.apply_bootstrap !== false
            && window.ExplorerAPI?.applyPreWarmedTaskCursor
            && opts.task_context?.cursor_bootstrap
        ) {
            await window.ExplorerAPI.applyPreWarmedTaskCursor({
                cursor_id: cursorId,
                context: opts.task_context,
            });
        }
        return this;
    }

    async previewPointer(pointerType, pointerName, options = {}) {
        if (!pointerName || !window.ExplorerAPI?.fetchGraphDataForPointer) {
            return null;
        }
        if (this.explorer && typeof this.explorer._showLoading === 'function') {
            this.explorer._showLoading(null, { sequential: true });
        }
        let data;
        try {
            data = await window.ExplorerAPI.fetchGraphDataForPointer(pointerType, pointerName);
        } finally {
            if (this.explorer && typeof this.explorer._hideLoading === 'function') {
                this.explorer._hideLoading();
            }
        }
        if (data && !data.error) {
            this.setData(data);
            this._applyGraphAppearance();
            if (pointerType === 'bookmark' && typeof window.ExplorerTabs !== 'undefined' && window.ExplorerTabs.setBookmarks) {
                if (options.bookmarkList) {
                    window.ExplorerTabs.setBookmarks(options.bookmarkList, pointerName);
                } else {
                    window.ExplorerTabs.setBookmarks(undefined, pointerName);
                }
            }
            if (pointerType === 'checkpoint' && typeof window.ExplorerTabs !== 'undefined' && window.ExplorerTabs.setCheckpoints) {
                if (options.checkpointList) {
                    window.ExplorerTabs.setCheckpoints(options.checkpointList, pointerName);
                } else {
                    window.ExplorerTabs.setCheckpoints(undefined, pointerName);
                }
            }
        }
        return data;
    }

    async navigateToBookmark(bookmarkName, options = {}) {
        if (!bookmarkName || !window.ExplorerAPI?.navigateToBookmark) {
            return null;
        }
        if (options.previewOnly && window.ExplorerAPI.fetchGraphDataForPointer) {
            return this.previewPointer('bookmark', bookmarkName, options);
        }
        return window.ExplorerAPI.navigateToBookmark(bookmarkName, options);
    }
    
    /**
     * Execute a search via ExplorerAPI.searchAsk (A2A task flow).
     * Creates an A2A task with sibyl agent, refreshes the graph,
     * and opens the task card window — exactly mirroring the graph
     * extension's native entity-input search flow.
     * 
     * @param {string} text - Natural language search query
     * @param {Array} keywords - Pre-identified entities [{id, label, type}]
     * @param {Object} options - { resetCursor, cursorId, openTaskCard, waitForCompletion }
     */
    async searchAsk(text, keywords = [], options = {}) {
        if (typeof window.ExplorerAPI !== 'undefined' && window.ExplorerAPI.searchAsk) {
            return window.ExplorerAPI.searchAsk(text, keywords, {
                cursorId: this.options.cursor_id || 'me',
                ...options,
            });
        }
        
        console.warn('[MerlinGraph] ExplorerAPI.searchAsk not available — falling back to direct task creation');
        throw new Error('ExplorerAPI.searchAsk not available');
    }
    
    // ============================================
    // Serialization
    // ============================================
    
    toJSON() {
        return this.explorer.toJSON();
    }
    
    // ============================================
    // Cleanup
    // ============================================
    
    destroy() {
        this.stopPolling();
        if (this._cursorChangedHandler) {
            document.removeEventListener('explorer:cursorChanged', this._cursorChangedHandler);
            this._cursorChangedHandler = null;
        }
        // Destroy chat module before wiping the DOM
        if (window.ExplorerChat) {
            window.ExplorerChat.destroy();
        }
        this.explorer.destroy();
    }
}

// Export GraphExplorer for advanced usage
export { GraphExplorer };

// Default export is MerlinGraph
export default MerlinGraph;

// Auto-register to window for UMD builds
if (typeof window !== 'undefined') {
    window.MerlinGraph = MerlinGraph;
    // Only set GraphExplorerClass - don't overwrite GraphExplorer from explorer-core.js
    // explorer-core.js sets window.GraphExplorer as an object with UI methods (toggleTheme, etc.)
    // We expose the class separately for advanced usage
    window.GraphExplorerClass = GraphExplorer;
}
